<template>
  <div class="card">
    <div class="card-body">
      <div class="soft-gradient p-3 rounded d-flex align-items-center justify-content-between mb-4">
        <div class="d-flex align-items-center">
          <div class="bg-primary-light p-2 rounded-2 me-2">
            <vue-feather type="edit-3" stroke="#652aa3" size="20"></vue-feather>
          </div>
          <h5 class="mb-0">Self Review</h5>
        </div>
        <div v-if="review?.status">
          <span :class="getStatusBadgeClass(review.status)" class="p-2">
            {{ formatStatus(review.status) }}
          </span>
        </div>
      </div>

      <div v-if="review?.responses && review.responses.length">
        <div v-for="section in categorizedResponses" :key="section.name" class="mb-4">
          <h6 class="text-primary mb-3">{{ section.name }}</h6>

          <div v-for="(response, idx) in section.responses" :key="idx" class="mb-3 p-3 border rounded">
            <h6 class="fw-semibold">{{ response.question_text || response.criteria?.question }}</h6>

            <div v-if="response.response" class="mb-2">
              <span>Response: {{ response.response }}</span>
            </div>

            <!-- Existing score display -->
            <div v-if="response.score != null" class="mb-2">
              <strong>Rating:</strong>
              <span class="badge bg-primary ms-2">{{ response.score }}/{{ response.criteria?.max_score || 5 }}</span>
            </div>

            <!-- Unscored: reviewer can rate 1..5 and see weight -->
            <div v-else-if="isReviewer" class="mb-2">
              <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div class="btn-group flex-wrap gap-2" role="group" :aria-label="`Rating options for response ${response.id}`">
                  <input type="radio" class="btn-check" :id="`resp_${response.id}_1`" :name="`resp_rating_${response.id}`" :value="1" v-model.number="scoresDraft[response.id]">
                  <label class="btn btn-outline-danger" :for="`resp_${response.id}_1`">Poor (1)</label>

                  <input type="radio" class="btn-check" :id="`resp_${response.id}_2`" :name="`resp_rating_${response.id}`" :value="2" v-model.number="scoresDraft[response.id]">
                  <label class="btn btn-outline-warning" :for="`resp_${response.id}_2`">Fair (2)</label>

                  <input type="radio" class="btn-check" :id="`resp_${response.id}_3`" :name="`resp_rating_${response.id}`" :value="3" v-model.number="scoresDraft[response.id]">
                  <label class="btn btn-outline-info" :for="`resp_${response.id}_3`">Good (3)</label>

                  <input type="radio" class="btn-check" :id="`resp_${response.id}_4`" :name="`resp_rating_${response.id}`" :value="4" v-model.number="scoresDraft[response.id]">
                  <label class="btn btn-outline-primary" :for="`resp_${response.id}_4`">Very Good (4)</label>

                  <input type="radio" class="btn-check" :id="`resp_${response.id}_5`" :name="`resp_rating_${response.id}`" :value="5" v-model.number="scoresDraft[response.id]">
                  <label class="btn btn-outline-success" :for="`resp_${response.id}_5`">Excellent (5)</label>
                </div>
                <div class="ms-auto">
                  <span class="badge bg-secondary">Weight: {{ response.criteria?.weight ?? 'N/A' }}</span>
                </div>
              </div>
            </div>

            <div v-if="response.comments" class="mb-2">
              <strong>Comments:</strong>
              <p class="mb-1">{{ response.comments }}</p>
            </div>
          </div>
        </div>

        <div v-if="review.goals_next_period" class="mb-3">
          <h6 class="text-primary">Goals for Next Period</h6>
          <p>{{ review.goals_next_period }}</p>
        </div>

        <div v-if="review.development_areas" class="mb-3">
          <h6 class="text-primary">Development Areas</h6>
          <p>{{ review.development_areas }}</p>
        </div>

        <!-- Submit scores button (only reviewer and when there are pending + selected scores) -->
        <div v-if="isReviewer && pendingCount > 0" class="d-flex justify-content-end mt-3">
          <button
            class="btn btn-primary"
            :disabled="submitting || !allPendingRated"
            @click="submitScores"
          >
            <span v-if="submitting" class="spinner-border spinner-border-sm me-2"></span>
            <i v-else class="icon-feather-send me-2"></i>
            Submit Scores
          </button>
        </div>
      </div>
      <div v-else class="alert alert-warning">
        No self review responses found.
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { usePerformanceStore } from '@/stores/performance';
import { decrypt } from '@/utils/crypto';
import notify from '@/utils/notify';

export default {
  name: 'SelfReviewResponses',
  props: {
    review: {
      type: Object,
      required: true
    }
  },
  emits: ['scores-submitted'],
  setup(props, { emit }) {
    const performanceStore = usePerformanceStore();
    const submitting = computed(() => performanceStore.submitting);
    const scoresDraft = ref({}); // { [responseId]: score }

    const formatStatus = (status) => {
      const statusMap = {
        draft: 'Draft',
        self_review: 'Self Review',
        manager_review: 'Manager Review',
        calibration: 'Calibration',
        completed: 'Completed',
        acknowledged: 'Acknowledged'
      };
      return statusMap[status] || status;
    };

    const getStatusBadgeClass = (status) => {
      const classes = {
        draft: 'badge bg-secondary',
        pending: 'badge bg-secondary',
        self_review: 'badge bg-warning',
        manager_review: 'badge bg-info',
        calibration: 'badge bg-primary',
        completed: 'badge bg-success',
        acknowledged: 'badge bg-success'
      };
      return classes[status] || 'badge bg-secondary';
    };

    const categorizedResponses = computed(() => {
      if (!props.review?.responses) return [];
      const sections = {};
      const responses = Array.isArray(props.review.responses) ? props.review.responses : [];
      responses.forEach(response => {
        const sectionName = response.section_title || response.criteria?.section || 'General';
        if (!sections[sectionName]) {
          sections[sectionName] = { name: sectionName, responses: [] };
        }
        sections[sectionName].responses.push(response);
      });
      return Object.values(sections);
    });

    // Reviewer check
    const isReviewer = computed(() => {
      try {
        const profile = JSON.parse(decrypt(sessionStorage.getItem('profile')));
        return profile?.id && props.review?.reviewer_id && profile.id === props.review.reviewer_id;
      } catch {
        return false;
      }
    });

    // Pending responses to be scored
    const pendingResponses = computed(() => {
      const resps = Array.isArray(props.review?.responses) ? props.review.responses : [];
      return resps.filter(r => r.score == null);
    });

    const pendingCount = computed(() => pendingResponses.value.length);

    const allPendingRated = computed(() => {
      if (pendingResponses.value.length === 0) return false;
      return pendingResponses.value.every(r => !!scoresDraft.value[r.id]);
    });

    const submitScores = async () => {
      try {
        if (!isReviewer.value) return;
        // Validate all pending have ratings
        const missing = pendingResponses.value.filter(r => !scoresDraft.value[r.id]);
        if (missing.length > 0) {
          notify.alert.error('Please rate all pending items before submitting');
          return;
        }

        const payload = {
          scores: pendingResponses.value.map(r => ({ response_id: r.id, score: scoresDraft.value[r.id] }))
        };

        const updated = await performanceStore.updateScores(props.review.id, payload);
        // Clear local draft for those responses
        scoresDraft.value = {};
        // Notify parent so it can refresh and/or advance step
        emit('scores-submitted', updated);
      } catch (e) {
        // errors surfaced via store notify
      }
    };

    return {
      submitting,
      scoresDraft,
      formatStatus,
      getStatusBadgeClass,
      categorizedResponses,
      isReviewer,
      pendingCount,
      allPendingRated,
      submitScores
    };
  }
};
</script>

<style scoped>
/* Modern header/banner helpers */
.bg-primary-light {
  background-color: rgba(101, 42, 163, 0.08);
}
.soft-gradient {
  background: linear-gradient(135deg, rgba(101,42,163,0.06) 0%, rgba(13,110,253,0.04) 100%);
  border-radius: 0.5rem;
}

/* Button check selected styles (match FeedbackForm) */
.btn-check:checked + .btn-outline-danger {
  background-color: #dc3545;
  color: white;
}
.btn-check:checked + .btn-outline-warning {
  background-color: #ffc107;
  color: black;
}
.btn-check:checked + .btn-outline-info {
  background-color: #17a2b8;
  color: white;
}
.btn-check:checked + .btn-outline-primary {
  background-color: #007bff;
  color: white;
}
.btn-check:checked + .btn-outline-success {
  background-color: #28a745;
  color: white;
}
</style>
